import { useFormContext } from 'react-hook-form';
import Grid from '@mui/material/Grid';

import { useLoadData } from '../../../hooks/useLoadData';
import { ApiResponse } from '../../../models/api-response';
import { FormInput } from '../../../components/hook-form/form-input.component';
import { FormInputTime } from '../../../components/hook-form/form-input.time.component';
import { servicesApiKeyStore } from '../../../api/masters/services-api-key-store';
import { FormSelectDynamic } from '../../../components/hook-form/form-select.dynamic.component';
import { FormAutoCompleteMultiSelectDynamic } from '../../../components/hook-form/form-autocomplete-multi-select.dynamic.component';
import { skillSetApiKeyStore } from '../../../api/masters/skill-set-api-key-store';
import { equipmentApiKeyStore } from '../../../api/masters/equipment-api-key-store';
import { serviceGroupApiKeyStore } from '../../../api/masters/service-group-api-key-store';
import { FormCheckbox } from '../../../components/hook-form/form-checkbox.component';
import { FormSelect } from '../../../components/hook-form/form-select.component';
import { serviceCategoryApiKeyStore } from '../../../api/masters/service-category-api-key-store';
import { FormCurrencyInput } from '../../../components/hook-form/form-currency-input.component';
import { marketAreaApiKeyStore } from '../../../api/masters/market-area-api-key-store';
import { Link, Typography } from '@mui/material';
import { useAuthorize } from '../../../hooks/use-authorize';
import { UserPermissionName } from '../../../models/user-permission-name';
import { useState } from 'react';
import { QuickBookServiceDialog } from './quickbook-service.dialog';

const styling = {
  boxStyle: { border: '1px solid #D5D8DC', borderRadius: 10, padding: 10 },
  dialogTitleStyle: { padding: '16px 24px 0' },
  contentPadding: { paddingTop: '30px' },
};

const isDUXEnabled = process.env.REACT_APP_DUX_FLAG === 'true';

export const ServiceForm = () => {
  const { setValue, getValues } = useFormContext();
  const serviceId = getValues('serviceID');
  const { data, isLoading } = useLoadData<ApiResponse<any>>(servicesApiKeyStore.getService(serviceId));

  if (data) {
    setValue('oneStoryServicePredecessors', data?.data?.oneStoryServicePredecessors);
    setValue('twoStoryServicePredecessors', data?.data?.twoStoryServicePredecessors);
    setValue('serviceSkillsets', data?.data?.serviceSkillsets);
    setValue('serviceEquipments', data?.data?.serviceEquipments);
    setValue('serviceMarketAreas', data?.data?.serviceMarketAreas);
  }

  const applicableOptions = [
    {
      value: true,
      label: 'Yes',
      isDefault: false,
    },
    {
      value: false,
      label: 'No',
      isDefault: true,
    },
  ];
  const planReviewOption = [
    {
      value: true,
      label: 'Yes',
      isDefault: false,
    },
    {
      value: false,
      label: 'No',
      isDefault: true,
    },
  ];

  const ppiOption = [
    {
      value: 'N/A',
      label: 'N/A',
      isDefault: false,
    },
    {
      value: 'PPI Only',
      label: 'PPI Only',
      isDefault: false,
    },
    {
      value: 'Forward',
      label: 'Forwards from Initial Inspection',
      isDefault: false,
    },
  ];

  const thirdPartyOption = [
    {
      value: 'N/A',
      label: 'N/A',
      isDefault: false,
    },
    {
      value: 'Third-Party Only',
      label: 'Third-Party Only',
      isDefault: false,
    },
    {
      value: 'Forward',
      label: 'Forwards from Initial Inspection',
      isDefault: false,
    },
  ];

  const thirdPartyAndPPIOption = [
    {
      value: 'N/A',
      label: 'N/A',
      isDefault: false,
    },
    {
      value: 'PPI Only',
      label: 'PPI Only',
      isDefault: false,
    },
    {
      value: 'Third-Party Only',
      label: 'Third-Party Only',
      isDefault: false,
    },
    {
      value: 'Third-Party and PPI',
      label: 'Third-Party and PPI',
      isDefault: false,
    },
    {
      value: 'Forward',
      label: 'Forwards from Initial Inspection',
      isDefault: false,
    },
  ];

  const isSyncWithDuxPermission = useAuthorize([UserPermissionName?.SYNC_WITH_DUX]);
  const isViewQuickBooksServiceMappingPermission = useAuthorize([UserPermissionName?.VIEW_QB_SERVICE_MAPPING]);


  const [openDialog, setOpenDialog] = useState<boolean>(false);

  const handleClickOpen = (event: any) => {
    event.preventDefault(); // prevent default navigation of link
    setOpenDialog(true);
  };

  const handleClosedDialog = () => {
    setOpenDialog(false);
  }

  return (
    <>
      <Grid container spacing={2}>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormInput label="Service Name" name="serviceName" maxLength={150} autoFocus />
        </Grid>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormInput label="Service Code" name="serviceCode" maxLength={150} disabled={!!serviceId} />
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <FormInput label="Description" name="description" maxLength={150} multiline={true} />
        </Grid>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormSelectDynamic
            name="serviceGroupID"
            label="Service Group"
            getQueryKeyConfig={() => serviceGroupApiKeyStore.getServiceGroups()}
            optionsValueKey="serviceGroupID"
            optionsLabelKeys={['serviceGroupName']}
          />
        </Grid>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormSelectDynamic
            name="serviceCategoryID"
            label="Service Category"
            getQueryKeyConfig={() => serviceCategoryApiKeyStore.getServiceCategories()}
            optionsValueKey="serviceCategoryID"
            optionsLabelKeys={['serviceCategoryName']}
          />
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <div style={styling.boxStyle}>
            <div style={{ paddingBottom: 15 }}>
              <Typography variant="body1" color="textSecondary">
                Service Type for FastField
              </Typography>
            </div>
            <Grid container spacing={2}>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <FormSelectDynamic
                  name="serviceTypeID"
                  label="Fastfield Service Category (Lot) = PPI Only"
                  required
                  getQueryKeyConfig={() => servicesApiKeyStore.getServiceTypes()}
                  optionsValueKey="serviceTypeID"
                  optionsLabelKeys={['serviceTypeName']}
                />
              </Grid>
              <Grid item lg={6} sm={6} xl={6} xs={6}>
                <FormSelectDynamic
                  name="notPPIOnlyServiceTypeID"
                  label="Fastfield Service Category (Lot) != PPI Only"
                  required
                  getQueryKeyConfig={() => servicesApiKeyStore.getServiceTypes()}
                  optionsValueKey="serviceTypeID"
                  optionsLabelKeys={['serviceTypeName']}
                />
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <div style={styling.boxStyle}>
            <div style={{ paddingBottom: 15 }}>
              <Typography variant="body1" color="textSecondary">
                FastField Service Category (Service)
              </Typography>
            </div>
            <Grid container spacing={2}>
              <Grid item lg={4} sm={4} xl={4} xs={4}>
                <FormSelect label="Third-Party Only" name="ffServiceCat3rdPartyOnly" options={thirdPartyOption} />
              </Grid>
              <Grid item lg={4} sm={4} xl={4} xs={4}>
                <FormSelect
                  label="Third-Party and PPI"
                  name="ffServiceCat3rdPartyAndPPI"
                  options={thirdPartyAndPPIOption}
                />
              </Grid>
              <Grid item lg={4} sm={4} xl={4} xs={4}>
                <FormSelect label="PPI Only" name="ffServiceCatPPIOnly" options={ppiOption} />
              </Grid>
            </Grid>
          </div>
        </Grid>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="serviceMarketAreas"
            label="Applicable Market Areas"
            getQueryKeyConfig={() => marketAreaApiKeyStore.getMarketAreas()}
            optionsLabelKey="marketAreaName"
            optionsValueKey="marketAreaID"
          />
        </Grid>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormCurrencyInput label="Invoice Amount ($)" name="price" />
        </Grid>
        {/* <Grid item lg={6} sm={6} xl={6} xs={6}>
        <FormCurrencyInput label="Accrual Amount ($)" name="accrualAmount" />
      </Grid> */}
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="serviceSkillsets"
            label="Required Skills"
            getQueryKeyConfig={() => skillSetApiKeyStore.getSkillSets()}
            optionsLabelKey="skillsetName"
            optionsValueKey="skillsetID"
          />
        </Grid>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="serviceEquipments"
            label="Required Equipments"
            getQueryKeyConfig={() => equipmentApiKeyStore.getEquipments()}
            optionsLabelKey="equipmentName"
            optionsValueKey="equipmentID"
          />
        </Grid>

        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="oneStoryServicePredecessors"
            label="1-Story Predecessor"
            getQueryKeyConfig={() => servicesApiKeyStore.getInitialServices()}
            optionsLabelKey="serviceName"
            optionsValueKey="serviceID"
          />
        </Grid>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormAutoCompleteMultiSelectDynamic
            name="twoStoryServicePredecessors"
            label="2-Story Predecessor"
            getQueryKeyConfig={() => servicesApiKeyStore.getInitialServices()}
            optionsLabelKey="serviceName"
            optionsValueKey="serviceID"
          />
          {/* <FormSelectDynamic
          name="predecessorServiceID"
          label="Predecessor"
          getQueryKeyConfig={() => servicesApiKeyStore.getServices()}
          optionsValueKey="serviceID"
          optionsLabelKeys={['serviceName']}
        /> */}
        </Grid>

        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormSelectDynamic
            name="billingTypeID"
            label="Billing Type"
            required
            getQueryKeyConfig={() => servicesApiKeyStore.getServiceBillingTypes()}
            optionsValueKey="billingTypeID"
            optionsLabelKeys={['billingType']}
          />
        </Grid>
        {/* <Grid item lg={6} sm={6} xl={6} xs={6}>
        <FormSelectDynamic
          name="qboAccountID"
          label="Income Account"
          required
          getQueryKeyConfig={() => servicesApiKeyStore.getQBOAccounts()}
          optionsValueKey="qboAccountID"
          //optionsLabelKeys={['qboFullyQualifiedName','qboAccountType','qboaccountSubType','qboAccountID','qboparentAccountID']}
          optionsLabelKeys={['qboFullyQualifiedName']}
        />
      </Grid> */}
        {/* <Grid item lg={6} sm={6} xl={6} xs={6}>
        <FormInputTime name="cutOffTime" label="Cut-off Time" />
      </Grid> */}
        {/* <Grid item lg={6} sm={6} xl={6} xs={6}>
        <FormInput type="number" label="Completion Hours" name="completionHours" />
      </Grid> */}
        {/* <Grid item lg={6} sm={6} xl={6} xs={6}>
        <FormSelect name="isPassFailedApplicable" label="Pass/Fail Applicable" options={applicableOptions} />
      </Grid> */}
        {/* <Grid item lg={6} sm={6} xl={6} xs={6}>
        <FormSelectDynamic
          name="retestServiceID"
          label="Retest Service"
          getQueryKeyConfig={() => servicesApiKeyStore.getServices()}
          optionsValueKey="serviceID"
          optionsLabelKeys={['serviceName']}
        />
      </Grid> */}
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormSelect label="Plan Review" name="isPlanReviewRequired" options={planReviewOption} />
        </Grid>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormInput label="FastField FormID" name="fastFieldFormID" />
        </Grid>
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormInput label="Expected Time (Minutes)" name="expectedTime" />
        </Grid>
        {(serviceId && !data?.data?.isRetestService) && (
        <Grid item lg={6} sm={6} xl={6} xs={6}>
          <FormInput label="Reinspection Expected Time (Minutes)" name="reinspectionExpectedTime" />
        </Grid>
        )}
        {(serviceId && isViewQuickBooksServiceMappingPermission) && (
          <Grid item lg={12} sm={12} xl={12} xs={12}>
            <Grid
              container
              direction="row"
              sx={{
                justifyContent: "flex-end",
                alignItems: "flex-start",
              }}
            >
              <Typography variant="body1" style={{ paddingTop: 6 }}>
                <Link href="#" underline="hover" onClick={handleClickOpen}>
                  QuickBooks Service Mapping
                </Link>
              </Typography>
            </Grid>
          </Grid>
        )}
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <Grid container direction="row" justifyContent="flex-end" alignItems="flex-start">
            {/* <FormCheckbox label="Auto Retest on Failure" name="autoRetestOnFailure" /> */}
            <FormCheckbox label="QuickBooks Service" name="isQBService" />
            <FormCheckbox label="Retest Service" name="isRetestService" />
            {isDUXEnabled && isSyncWithDuxPermission && <FormCheckbox label="Sync With DUX" name="syncWithDux" />}
            <FormCheckbox label="DUX Service" name="isDuxService" />
            <FormCheckbox label="Dux Allow To Schedule" name="allowToScheduleFromDux" />
            <FormCheckbox label="Dux Customer Report View" name="customerReportViewDux" />
            <FormCheckbox label="Active" name="isActive" />
          </Grid>
        </Grid>
        <Grid item lg={12} sm={12} xl={12} xs={12}>
          <div style={{ marginRight: 'auto', paddingLeft: '16px' }}>
            <p style={{ marginBottom: '25px', lineHeight: '20px' }}>
              <strong>Note:</strong>
              <i>
                <ul>
                  <li style={{ marginLeft: '16px' }}>
                    Changes to DUX-related flags will appear in DUX only after the corresponding projects have been updated in SI Track.
                  </li>
                  <li style={{ marginLeft: '16px' }}>
                    Selecting the DUX Service checkbox activates DUX Ad-hoc call functionality.
                  </li>
                </ul>
              </i>
            </p>
          </div>
        </Grid>
      </Grid>
      {openDialog && (
        <QuickBookServiceDialog
          serviceID={serviceId}
          qboServiceID={data?.data?.qboServiceID}
          reinspectionQBOServiceID={data?.data?.reinspectionQBOServiceID}
          openDialog={openDialog}
          onClosed={handleClosedDialog} />
      )}
    </>
  );
};
